"use client";
import { ReactNode } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils/utils";
import { modalRow } from "@/lib/animation/variant";

interface InfoTileCardProps {
  /** Circle on the left — an icon for a fact, initials for a person. */
  leading: ReactNode;
  /** Small uppercase caption, e.g. "When". Omitted for people. */
  label?: string;
  /** The line that matters: a time, a duration, a name. */
  value: ReactNode;
  /** Muted second line — a timezone, an email address. */
  sub?: ReactNode;
  /** Pinned to the right, e.g. the HOST badge. */
  trailing?: ReactNode;
  /** Stagger position, passed through to the shared modal reveal. */
  index?: number;
  className?: string;
}

// One row of the booking confirmation: circle, two lines of text, optional
// badge. Participants and the when/duration/where facts render through this —
// they were duplicated blocks with the same anatomy and drifting padding.
const InfoTileCard = ({
  leading,
  label,
  value,
  sub,
  trailing,
  index = 0,
  className,
}: InfoTileCardProps) => {
  return (
    <motion.div
      variants={modalRow}
      custom={index}
      initial="hidden"
      animate="visible"
      className={cn(
        "flex min-w-0 items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 transition-colors duration-300 hover:border-primary/40",
        className,
      )}
    >
      <span className="shrink-0">{leading}</span>

      <div className="min-w-0 flex-1">
        {label && (
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">
            {label}
          </p>
        )}
        {/* Wrapped, not truncated: on a narrow screen a value that wraps is
            more useful than one that is clipped. */}
        <p className="wrap-break-word text-sm font-medium text-white">
          {value}
        </p>
        {sub && (
          <p className="truncate text-[11px] font-medium text-white/40">
            {sub}
          </p>
        )}
      </div>

      {trailing && <span className="shrink-0">{trailing}</span>}
    </motion.div>
  );
};

export default InfoTileCard;
